import { useMemo, useState } from 'react';
import Link from 'next/link';
import { FormState, FieldState } from 'formstate';
import { useTranslation } from 'next-i18next';
import Heading from '@components/Heading/Heading';
import Input from '@components/Input/Input';
import Button from '@components/Button/Button';
import NextReturnsApi from '@features/api/nextApiService/nextReturnsApi';
import { emailValid, required } from '@features/userPanel/mobx/stores/validators';
import { TUserOrderType } from '@features/userPanel/typescript';
import styles from '@features/returns/components/ReturnsOrderLookupForm/ReturnsOrderLookupForm.styles';
type PropsType = {
  onOrderFound: (order: TUserOrderType) => void;
};
const ReturnsOrderLookupForm = ({
  onOrderFound
}: PropsType) => {
  const {
    t
  } = useTranslation('user');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const lookupForm = useMemo(() => new FormState({
    orderNumber: new FieldState<string>('').validators(required()).disableAutoValidation(),
    email: new FieldState<string>('').validators(required(), emailValid()).disableAutoValidation()
  }), []);
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const validationResult = await lookupForm.validate();
      if (validationResult.hasError) {
        setIsSubmitting(false);
        return;
      }
      const response = await NextReturnsApi.lookupOrder({
        orderNumber: lookupForm.$.orderNumber.value.trim(),
        email: lookupForm.$.email.value.trim()
      });
      if (response.error || !response.success || !response.data) {
        if (response.status === 429) {
          setSubmitError(t('returnsPage.lookup.error.rateLimited'));
        } else if (response.status === 400) {
          setSubmitError(t('returnsPage.lookup.error.notEligible'));
        } else {
          setSubmitError(t('returnsPage.lookup.error.notFound'));
        }
        setIsSubmitting(false);
        return;
      }
      onOrderFound(response.data);
    } catch (error) {
      console.error('Error looking up return order:', error);
      setSubmitError(t('returnsPage.lookup.error.generic'));
    } finally {
      setIsSubmitting(false);
    }
  };
  return <div sx={styles.formCard}>
      <Heading h={4} headingType={4} align="left" addSx={styles.formTitle}>
        {t('returnsPage.lookup.formTitle')}
      </Heading>

      <div sx={styles.inputContainer}>
        <Input field={lookupForm.$.orderNumber} placeholder={t('returnsPage.lookup.orderNumberPlaceholder')} cy="ReturnLookupOrderNumberInput" />
      </div>

      <div sx={styles.inputContainer}>
        <Input field={lookupForm.$.email} placeholder={t('returnsPage.lookup.emailPlaceholder')} cy="ReturnLookupEmailInput" />
      </div>

      <Link href="/login" sx={styles.customerPanelLink}>
        {t('returnsPage.lookup.customerPanelLink')}
      </Link>

      {submitError && <div sx={styles.errorMessage}>{submitError}</div>}

      <Button variant="default" variantColor="dark" onClick={handleSubmit} disabled={isSubmitting} fullWidth addSx={styles.submitButton} cy="ReturnLookupSubmitButton">
        {t('returnsPage.lookup.submitButton')}
      </Button>
    </div>;
};
export default ReturnsOrderLookupForm;